import {
  ChangeEvent,
  FocusEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { clamp, formatExternal, round } from '../stepper.helpers';
import { useStepperKeyboard } from './use-stepper-keyboard';

type UseStepperOptions = {
  value: number | undefined;
  onChange?: (value: number | undefined) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  step: number;
  min: number;
  max: number;
  allowDecimals: boolean;
  precision: number;
  disabled?: boolean;
};

type UseStepperReturn = {
  inputValue: string;
  currentValue: number;
  handleInputChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleBlur: (event: FocusEvent<HTMLInputElement>) => void;
  handleStep: (direction: 'up' | 'down') => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

export const useStepper = ({
  value,
  onChange,
  onBlur,
  step,
  min,
  max,
  allowDecimals,
  precision,
  disabled,
}: UseStepperOptions): UseStepperReturn => {
  const [prevValue, setPrevValue] = useState<number | undefined>(value);
  const [inputValue, setInputValue] = useState<string>(() =>
    formatExternal(value, allowDecimals, precision)
  );

  if (prevValue !== value) {
    setPrevValue(value);
    setInputValue(formatExternal(value, allowDecimals, precision));
  }

  const currentValue = parseFloat(inputValue) || 0;

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (value === undefined) {
      return;
    }

    const normalized = round(value, allowDecimals, precision);

    if (normalized !== value) {
      onChangeRef.current?.(normalized);
    }
  }, [value, allowDecimals, precision]);

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      let raw = e.target.value.replace(',', '.');

      if (!allowDecimals && !/^-?\d*$/.test(raw)) {
        return;
      }

      if (!/^-?\d*\.?\d*$/.test(raw)) {
        return;
      }

      if (allowDecimals && raw.includes('.')) {
        const [intPart, decPart = ''] = raw.split('.');

        if (decPart.length > precision) {
          raw = `${intPart}.${decPart.slice(0, precision)}`;
        }
      }

      setInputValue(raw);

      const parsed = parseFloat(raw);

      if (!isNaN(parsed)) {
        onChange?.(clamp(parsed, min, max));
      } else if (raw === '') {
        onChange?.(undefined);
      }
    },
    [allowDecimals, precision, min, max, onChange]
  );

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      if (inputValue === '' || inputValue === '-') {
        setInputValue('');
        onChange?.(undefined);
        onBlur?.(event);

        return;
      }

      const parsed = parseFloat(inputValue);
      const normalized = round(
        clamp(isNaN(parsed) ? currentValue : parsed, min, max),
        allowDecimals,
        precision
      );

      setInputValue(String(normalized));
      onChange?.(normalized);
      onBlur?.(event);
    },
    [
      inputValue,
      currentValue,
      allowDecimals,
      precision,
      min,
      max,
      onChange,
      onBlur,
    ]
  );

  const handleStep = useCallback(
    (direction: 'up' | 'down') => {
      const next = round(
        clamp(currentValue + (direction === 'up' ? step : -step), min, max),
        allowDecimals,
        precision
      );
      setInputValue(String(next));
      onChange?.(next);
    },
    [currentValue, step, min, max, allowDecimals, precision, onChange]
  );

  const handleKeyDown = useStepperKeyboard({ disabled, onStep: handleStep });

  return {
    inputValue,
    currentValue,
    handleInputChange,
    handleBlur,
    handleStep,
    handleKeyDown,
  };
};
